import { differenceInDays } from 'date-fns';
import type { CSSProperties } from 'react';
import { FormattedDate, FormattedMessage, FormattedTime } from 'react-intl';
import styled from 'styled-components';

import { css } from '@sorare/css';

import { Shards } from '#src/atoms/icons/Shards.tsx';
import { SorareLogo } from '#src/atoms/icons/SorareLogo/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconBullseyeArrow } from '#src/atoms/icons/system/iconBullseyeArrow.ts';
import { iconCards } from '#src/atoms/icons/system/iconCards.ts';
import { iconClock } from '#src/atoms/icons/system/iconClock.ts';
import { iconRefresh } from '#src/atoms/icons/system/iconRefresh.ts';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import {
  BodyS,
  BrandT,
  BrandXS,
  LabelM,
} from '#src/atoms/typography/index.tsx';
import { Dialog } from '#src/components/dialog/index.tsx';
import { TimeLeft } from '#src/contexts/ticker/TimeLeft/index.tsx';
import { useFeatureFlags } from '#src/hooks/useFeatureFlags.tsx';
import { SetBrandingImage } from '#src/set/branding/SetBrandingImage.tsx';
import { useCardSetBranding } from '#src/set/branding/useCardSetBranding.ts';
import { useCommonGameSet } from '#src/set/index.ts';

import { TimeLeftBox } from './TimeLeftBox';

const StyledDialog = styled(Dialog)`
  background:
    linear-gradient(
      to bottom,
      transparent 0%,
      var(--c-black) calc(40 * var(--unit))
    ),
    var(--background);
`;
const Content = styled(Vertical).attrs({ gap: 3, center: true })`
  padding: 0 var(--triple-unit) var(--double-unit);
`;
const MainTextBlock = styled(Vertical).attrs({ gap: 2 })`
  align-items: flex-start;
  align-self: flex-start;
`;
const itemWrapper = css`
  display: grid;
  grid-template-columns: var(--triple-unit) 1fr;
  color: var(--c-nd-600);
`;

const items = ({
  setName,
  endDate,
  parsedSetLastGameDate,
}: {
  setName: string;
  endDate: Date;
  parsedSetLastGameDate: Date;
}) => [
  {
    key: '2',
    message: (
      <FormattedMessage
        defaultMessage="You won't be able to use your {setName} Essence in the next set"
        values={{ setName }}
      />
    ),
    icon: <Shards size={2} />,
  },
  {
    key: '3',
    message: (
      <FormattedMessage
        defaultMessage="You won't be able to use your {setName} Cards in the next set"
        values={{ setName }}
      />
    ),
    icon: <FontAwesomeIcon icon={iconCards} size="md" />,
  },
  {
    key: '4',
    message: (
      <FormattedMessage
        defaultMessage="{setName} Streaks are still eligible through your last game {day}, {month} {dayOfMonth, selectordinal, one {#st} two {#nd} few {#rd} other {#th}}"
        values={{
          setName,
          month: parsedSetLastGameDate && (
            <FormattedDate value={parsedSetLastGameDate} month="short" />
          ),
          dayOfMonth: parsedSetLastGameDate?.getDate(),
          day: <FormattedDate value={parsedSetLastGameDate} weekday="long" />,
        }}
      />
    ),
    icon: <FontAwesomeIcon icon={iconBullseyeArrow} size="md" />,
  },
  {
    key: '5',
    message: (
      <FormattedMessage
        defaultMessage="Streaks reset on {day}, {month} {dayOfMonth, selectordinal, one {#st} two {#nd} few {#rd} other {#th}} when the new set begins"
        values={{
          setName,
          month: <FormattedDate value={endDate} month="short" />,
          dayOfMonth: endDate.getDate(),
          day: <FormattedDate value={endDate} weekday="long" />,
        }}
      />
    ),
    icon: <FontAwesomeIcon icon={iconRefresh} size="md" />,
  },
];

type Props = { open: boolean; onClose: () => void; endDate: Date };
export const SetWillEndDialog = ({ open, onClose, endDate }: Props) => {
  const { setBackgroundUrl, setLogoUrl } = useCardSetBranding();
  const {
    flags: { setLastGameDate },
  } = useFeatureFlags();
  const background = setBackgroundUrl
    ? `url(${setBackgroundUrl}) center / cover no-repeat`
    : undefined;
  const parsedSetLastGameDate = new Date(setLastGameDate || '');
  const commonGameSet = useCommonGameSet();
  const displayName = commonGameSet.name;
  const diffInDays = differenceInDays(endDate, new Date());
  const lessThan1Day = diffInDays <= 1;
  const lessThan1Week = diffInDays < 7;

  const color = lessThan1Day ? 'var(--c-red-400)' : 'var(--c-yellow-400)';
  const daysLeft = differenceInDays(endDate, new Date());

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      style={{ '--background': background } as CSSProperties}
      fullWidth
      maxWidth="xs"
    >
      <Content>
        <Vertical gap={2} center>
          <Vertical gap={1}>
            <SorareLogo size="small" />
            <SetBrandingImage
              url={setLogoUrl}
              height={34}
              color="var(--c-white)"
            />
          </Vertical>
          <TimeLeftBox style={{ '--shadow-color': color } as CSSProperties}>
            <Horizontal gap={0.5} center>
              <FontAwesomeIcon color={color} icon={iconClock} size="xs" />
              <BrandXS color={color}>
                {lessThan1Day ? (
                  <TimeLeft time={endDate} />
                ) : (
                  <FormattedMessage
                    defaultMessage="{nbDays, plural, one {# day left} other {# days left}}"
                    values={{ nbDays: daysLeft }}
                  />
                )}
              </BrandXS>
            </Horizontal>
          </TimeLeftBox>
        </Vertical>
        <MainTextBlock>
          <Vertical gap={0.5}>
            <BrandT>
              {!!lessThan1Day && (
                <FormattedMessage
                  defaultMessage="{setName} set ends in less than <bold>24 hours.</bold>"
                  values={{
                    bold: chunks => (
                      <BrandT color={color} as="span" bold>
                        {chunks}
                      </BrandT>
                    ),
                    setName: displayName,
                  }}
                />
              )}
              {!!lessThan1Week && !lessThan1Day && (
                <FormattedMessage
                  defaultMessage="{setName} set ends this {day}."
                  values={{
                    day: <FormattedDate value={endDate} weekday="long" />,
                    setName: displayName,
                  }}
                />
              )}
              {!lessThan1Week && !lessThan1Day && (
                <FormattedMessage
                  defaultMessage="{setName} set ends on {month} {dayOfMonth, selectordinal, one {#st} two {#nd} few {#rd} other {#th}}."
                  values={{
                    month: <FormattedDate value={endDate} month="long" />,
                    dayOfMonth: endDate.getDate(),
                    setName: displayName,
                  }}
                />
              )}
            </BrandT>
            <BodyS>
              <FormattedMessage defaultMessage="Here's what to know to make the most of the last few weeks." />
            </BodyS>
          </Vertical>
          <Vertical>
            <LabelM bold>
              <FormattedMessage
                defaultMessage="On {day} at {time}:"
                values={{
                  day: <FormattedDate value={endDate} weekday="long" />,
                  time: (
                    <FormattedTime
                      value={endDate}
                      hour="2-digit"
                      minute="2-digit"
                    />
                  ),
                }}
              />
            </LabelM>
            {items({
              setName: displayName || '',
              endDate,
              parsedSetLastGameDate,
            }).map(({ message, key, icon }) => (
              <div className={itemWrapper} key={key}>
                {icon}
                <LabelM color="var(--c-nd-600)">{message}</LabelM>
              </div>
            ))}
          </Vertical>
        </MainTextBlock>
      </Content>
    </StyledDialog>
  );
};
